import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCircleCheck, faCircleX } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import {
  isUserDeleted,
  formatUserName,
  type PerformanceFilter,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
  getLinkNavigationProps,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { type ColumnOption } from "interfaces/columnOptions";
import { type FC, useEffect } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { selectPeopleDetails, selectPeopleDetailsValue } from "../state/slices/assessmentPerformanceSlice";
import { type AssessmentPeopleDetails } from "../types/state";
import { RouteNames } from "enums";

import styles from "./assessmentPerformancePeople.module.scss";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { MoboLink } from "components/MoboLink/MoboLink";
import dateTimeUtils from "../../../../utils/dateTimeUtils";

export type AssessmentPerformancePeopleProps = {
  filter: PerformanceFilter;
  hideControls?: boolean;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

export type EngagementLineStatus = "Fail" | "Pass";

type AssessmentStatusCompute = {
  [key in EngagementLineStatus]: {
    icon: IconProp;
  };
};

const AssessmentPerformancePeopleStatusToCell: AssessmentStatusCompute = {
  Pass: {
    icon: faCircleCheck,
  },
  Fail: {
    icon: faCircleX,
  },
};

export const AssessmentPerformancePeopleComponent: FC<AssessmentPerformancePeopleProps> = ({
  filter,
  peopleDetails,
  peopleDetailsStatus,
  hideControls,
  setHasExportData,
  includeAccounts,
  accountId,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const getColumnOptions = (
    accountId?: number,
    includeAccounts: boolean = false,
    childAccountId?: number,
    hasCreateAccountsAddOn?: boolean,
  ): ColumnOption<AssessmentPeopleDetails>[] => [
    {
      name: "NAME",
      width: 5,
      isSortable: false,
      render: (entity: AssessmentPeopleDetails) => {
        const formattedNameDisplay = formatUserName(entity.FirstName, entity.LastName);
        const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
        const shouldShowLink = !isUserDeleted(entity) && (!includeAccounts || childAccountEnabled || !childAccountId);
        const userName = (
          <Tooltip content={formattedNameDisplay} target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} />
        );
        const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
          entity.AccountId,
          accountId,
          childAccountId,
          hasCreateAccountsAddOn,
        );
        const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
          childAccountEnabled,
          isUserFromCurrentAccount,
          childAccountId,
          fallbackAccountId: entity.AccountId,
        });
        const secondaryInfo = [entity.UserTitle, entity.Department].filter((x) => x).join(", ");
        return (
          <div>
            {shouldShowLink ? (
              <MoboLink
                to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
                accountId={linkAccountId}
                className={styles.linkButton}
                target={linkTarget}
              >
                {userName}
              </MoboLink>
            ) : (
              userName
            )}
            <Tooltip
              content={secondaryInfo}
              target={<TextTruncate className="workTitle">{secondaryInfo}</TextTruncate>}
            />
          </div>
        );
      },
    },
    {
      width: 5,
      name: "EMAIL",
      isSortable: false,
      render: (entity: AssessmentPeopleDetails) => (
        <div>
          <Tooltip target={<TextTruncate>{entity.Email}</TextTruncate>} content={entity.Email} />
          <div>&nbsp;</div>
        </div>
      ),
    },
    {
      name: "DATE STARTED",
      width: 5,
      isSortable: false,
      render: (entity: AssessmentPeopleDetails) => {
        const date = dateTimeUtils.formatDate(entity.StartDate);

        return (
          <div>
            <div>{date}</div>
            <div>&nbsp;</div>
          </div>
        );
      },
    },
    {
      name: "DATE COMPLETED",
      width: 5,
      isSortable: false,
      render: (entity: AssessmentPeopleDetails) => {
        const date = entity.CompletionDate !== null ? dateTimeUtils.formatDate(entity.CompletionDate) : "-";

        return (
          <div>
            <div>{date}</div>
            <div>&nbsp;</div>
          </div>
        );
      },
    },
    {
      name: "RESULT",
      width: 5,
      isSortable: false,
      render: (entity: AssessmentPeopleDetails) => {
        return (
          <div>
            <FontAwesomeIcon
              color={reportingColors.greyLight}
              icon={AssessmentPerformancePeopleStatusToCell[entity.Result as keyof AssessmentStatusCompute]?.icon}
            />
            &nbsp;
            {entity.Result}
          </div>
        );
      },
    },
  ];
  const columnOptions = getColumnOptions(accountId, includeAccounts, childAccountId, hasCreateAccountsAddOn);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: peopleDetailsStatus.status === RStatus.Got && !!peopleDetails.length,
      tooltipContent: "No data available for export. This assessment contains no data.",
    });
  }, [setHasExportData, peopleDetailsStatus.status, peopleDetails]);

  return (
    <RequestStatusRenderer state={peopleDetailsStatus}>
      <GenericPerformanceList
        hideControls={hideControls}
        rows={peopleDetails}
        columns={columnOptions}
        filter={filter}
        title="People"
      />
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    peopleDetails: selectPeopleDetailsValue(state),
    peopleDetailsStatus: selectPeopleDetails(state),
    accountId: state.userProfile.accountId,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AssessmentPerformancePeopleComponent);
export default ConnectedComponent;
